import React, { useState, useEffect } from "react";
import { Task, ColumnType } from "../../types/types";
import DroppableColumn from "../DroppableColumn/DroppableColumn";
import StoryModal from "../StoryModal/StoryModal";
import { realAPI } from "../../services/realAPI";

interface Sprint {
  id: string;
  name: string;
  description: string;
  startDate: Date;
  endDate: Date;
  status: "planning" | "active" | "completed";
}

interface MainContentProps {
  initialTasks: Task[];
  sprints?: Sprint[];
  onUpdateTask?: (updatedTask: Task) => void;
}

const MainContent: React.FC<MainContentProps> = ({
  initialTasks,
  sprints = [],
  onUpdateTask,
}) => {
  const [tasks, setTasks] = useState<Task[]>(initialTasks);
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [statusKeys, setStatusKeys] = useState<string[]>(["todo", "inProgress", "deployed", "done"]);

  useEffect(() => {
    // Component mounted successfully
  }, []);

  useEffect(() => {
    // Update tasks when initialTasks prop changes
    console.log("MainContent: initialTasks changed:", initialTasks);
    setTasks(initialTasks);
  }, [initialTasks]);

  useEffect(() => {
    let mounted = true;
    const loadOrgSettings = async () => {
      try {
        const settings = await realAPI.getOrganizationSettings();
        if (!mounted) return;
        if (settings && Array.isArray(settings.workflowStatuses) && settings.workflowStatuses.length > 0) {
          setStatusKeys(settings.workflowStatuses);
        }
      } catch (err) {
        console.warn("Failed to load organization settings for workflow statuses, using defaults:", err);
      }
    };
    loadOrgSettings();
    return () => { mounted = false; };
  }, []);

  const handleDrop = (taskId: number, newTaskColumn: ColumnType) => {
    setTasks((prevTasks) => {
      const updated = prevTasks.map((task) =>
        task.id === taskId ? { ...task, taskColumn: newTaskColumn } : task
      );
      // Backend update for drag-and-drop
      const updatedTask = updated.find((task) => task.id === taskId);
      if (onUpdateTask && updatedTask) {
        onUpdateTask(updatedTask);
      }
      return updated;
    });
  };

  const handleCardClick = (task: Task) => {
    setSelectedTask(task);
    setIsModalOpen(true);
  };

  const handleModalClose = () => {
    setIsModalOpen(false);
    setSelectedTask(null);
  };

  const handleTaskUpdate = (updatedTask: Task) => {
    setTasks((prevTasks) =>
      prevTasks.map((task) => (task.id === updatedTask.id ? updatedTask : task))
    );
    setSelectedTask(updatedTask);

    // Propagate updates (e.g., story points/assignee/status changes) to parent
    if (onUpdateTask) {
      onUpdateTask(updatedTask);
    }
  };

  // Group tasks by dynamic status keys (preserve order)
  const tasksByStatus: Record<string, Task[]> = {};
  statusKeys.forEach((k) => (tasksByStatus[k] = []));
  tasks.forEach((t) => {
    const key = t.taskColumn || "todo";
    if (!tasksByStatus[key]) tasksByStatus[key] = [];
    tasksByStatus[key].push(t);
  });

  return (
    <>
      <main className="container mx-auto px-4 py-8">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {statusKeys.map((statusKey) => {
            // Basic label and color mapping for known keys
            const labelMap: Record<string, string> = {
              todo: "To Do",
              inProgress: "In Progress",
              deployed: "Deployed",
              done: "Done",
            };
            const colorMap: Record<string, string> = {
              todo: "bg-gray-200 text-gray-800",
              inProgress: "bg-blue-200 text-blue-800",
              deployed: "bg-purple-200 text-purple-800",
              done: "bg-green-200 text-green-800",
            };

            const title = labelMap[statusKey] || statusKey;
            const color = colorMap[statusKey] || "bg-gray-200 text-gray-800";
            const list = tasksByStatus[statusKey] || [];

            return (
              <DroppableColumn
                key={statusKey}
                title={title}
                tasks={list}
                count={list.length}
                color={color}
                column={statusKey as ColumnType}
                onDrop={handleDrop}
                onCardClick={handleCardClick}
              />
            );
          })}
        </div>
      </main>

      {/* Story Modal */}
      {selectedTask && (
        <StoryModal
          task={selectedTask}
          isOpen={isModalOpen}
          onClose={handleModalClose}
          onUpdate={handleTaskUpdate}
          sprints={sprints}
        />
      )}
    </>
  );
};

export default MainContent;
